import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import { IComment } from "../atoms/CommentsScrollView/Interfaces"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import CommentsScrollView from "../atoms/CommentsScrollView/CommentsScrollView"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import AttachmentButton from "../atoms/Buttons/AttachmentButton"
import IconButton from "../atoms/Buttons/IconButton"
import MultilineInput from "components/ProjectNonSpecificComponents/Elements/MultilineInput"
import FilesRepr from "./FilesRepr"
import { TErrorDetails, TOnUpdateFiles } from "components/ProjectNonSpecificComponents/Elements/UploadButton/Interfaces"
import { TFormModalMode } from "../../../Interfaces/GlobalInterface"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import Button from "../atoms/Buttons/Button/Button"

interface Props {
	data: IComment[]
	mode: TFormModalMode
	defaultFiles?: File[]
	files?: File[]
	/**
	 * Files that are put on display at the very bottom of this section.
	 * Files such as already previously added files.
	 * Basically a separate section for other files.
	 */
	onClickCommentSend?: TOnSendMessage
	onClickAttachmentSend?: () => void
	onUpdateAddedFiles?: TOnUpdateFiles
}

const CommentsSection = ({
	data,
	mode,
	defaultFiles = [],
	files,
	onClickCommentSend = () => {},
	onClickAttachmentSend = () => {},
	onUpdateAddedFiles = () => {},
}: Props) => {
	const isPreviewMode = mode == "preview"
	const { t } = useTranslation()
	const [text, setText] = useState<string>("")
	const [addedFiles, setAddedFiles] = useState<File[]>(files ?? defaultFiles)
	const [error, setError] = useState<TErrorDetails>()
	const hasNoMsgContent = text.trim().length == 0
	const store = useStore()

	useEffect(() => {
		onUpdateAddedFiles(addedFiles)
	}, [addedFiles])

	useEffect(() => {
		if (!files) return
		setAddedFiles(files)
	}, [files])

	const removeFileByIdx = (idx: number) => {
		setAddedFiles([...addedFiles.slice(0, idx), ...addedFiles.slice(idx + 1)])
	}

	useEffect(() => {
		error && store.setInfoModalConfig({
			type: "warning",
			titleKey: `${t("file.fail.reject")} ${error.invalidFilesNames}`,
			descriptionKey: `${t("file.fail.reject-ext")} ${error.acceptedFileExts}`,
		})
	}, [error])

	return (
		<Section id="comment-side-of-modal">
			<p className="font-bold uppercase">{t("rawText.comments")}</p>
			<hr />
			<Whitespace x="1" />
			<CommentsScrollView data={data} />
			<Whitespace x="1.75" />

			<HyperDiv direction="row" centeredY>
				<MultilineInput
					style={{ flex: 1, minHeight: "2.5em" }}
					value={text}
					placeholder={t("rawText.pleaseInputText")}
					disabled={isPreviewMode}
					onChange={(e) => setText(e.target.value)}
				/>
				<HyperDiv direction="row" height="2.7em" centeredY alignedSelfStart>
					<Whitespace h x="1" />
					<AttachmentButton
						files={addedFiles}
						disabled={isPreviewMode}
						onUpdateFiles={setAddedFiles}
						onError={(error) => setError(error)}
					/>
					<Whitespace h x="2" />
					<IconButton
						iconKey="send"
						iconColor={hasNoMsgContent ? "var(--muddy-puddle-gray)" : undefined}
						disabled={isPreviewMode}
						onClick={() => {
							if (hasNoMsgContent) {
								return store.setInfoModalConfig({
									titleKey: "rawText.missing-comment",
									type: "fail",
									showDuration: 2000,
								})
							}

							onClickCommentSend(text.trim())
							setText("")
						}}
					/>
					<Whitespace h x="1" />
				</HyperDiv>
			</HyperDiv>

			<Whitespace x="1" />
			<HyperDiv direction="row" spacedBetween>
				<FilesRepr data={addedFiles} onRemove={removeFileByIdx} />
				{files && files?.length > 0 && (
					<Button style={{ minWidth: 200, height: 50 }} onClick={onClickAttachmentSend}>
						{t("button.save-files")}
					</Button>
				)}
			</HyperDiv>
		</Section>
	)
}

export default CommentsSection

const Section = styled.div`
	padding: 16px;
	overflow: hidden;
`

export type TOnSendMessage = (message: string) => void
